/**
 * FGM ASCENT Collection — Exclusive Drop Landing Page.
 * - Live countdown to launch date
 * - Product gallery (3 colorways)
 * - Waitlist email signup (returns a unique unlock URL)
 * - Post-launch: colorway/size picker + add-to-cart
 * - Waitlist unlock: 24hr early access + discount code via ?key=xxx
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { useCart } from "../cart";
import { FGM } from "../brand";
import Header from "./Header";
import Footer from "./Footer";
import CartDrawer from "./CartDrawer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLUG = "ascent";

function useCountdown(targetIso) {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);
    return useMemo(() => {
        if (!targetIso) return null;
        const target = new Date(targetIso).getTime();
        const diff = Math.max(0, Math.floor((target - now) / 1000));
        const days = Math.floor(diff / 86400);
        const hours = Math.floor((diff % 86400) / 3600);
        const minutes = Math.floor((diff % 3600) / 60);
        const seconds = diff % 60;
        return { total: diff, days, hours, minutes, seconds, done: diff === 0 };
    }, [targetIso, now]);
}

function CountdownDigit({ label, value }) {
    const v = String(value ?? 0).padStart(2, "0");
    return (
        <div className="flex flex-col items-center min-w-[70px] md:min-w-[110px]">
            <div
                data-testid={`countdown-${label.toLowerCase()}`}
                className="font-display text-5xl md:text-7xl text-white tabular-nums tracking-tight leading-none"
            >
                {v}
            </div>
            <div className="mt-2 text-[10px] md:text-xs tracking-[0.35em] uppercase text-white/40 font-display">
                {label}
            </div>
        </div>
    );
}

const COLORWAY_META = {
    "Red/White": { swatch: "#c8102e", secondary: "#ffffff", label: "Red / White", emoji: "🔴" },
    "Black/White": { swatch: "#000000", secondary: "#ffffff", label: "Black / White", emoji: "⚫" },
    "Royal Blue/White": { swatch: "#1d4ed8", secondary: "#ffffff", label: "Royal Blue / White", emoji: "🔵" },
};

const COLORWAY_IMAGE = {
    "Red/White": "/products/ascent-red-white.webp",
    "Black/White": "/products/ascent-black-white.webp",
    "Royal Blue/White": "/products/ascent-royal-blue-white.webp",
};

// Piece variants — 3 SKUs available (set, jacket, shorts).
const PIECES = [
    { key: "set", label: "Full Set", price: 129, product_id: "fgm-ascent-collection", suffix: "" },
    { key: "jacket", label: "Jacket", price: 89, product_id: "fgm-ascent-jacket", suffix: "-jacket" },
    { key: "shorts", label: "Shorts", price: 65, product_id: "fgm-ascent-shorts", suffix: "-shorts" },
];

function pieceImage(colorway, pieceKey, view = "front") {
    const p = PIECES.find((x) => x.key === pieceKey) || PIECES[0];
    const base = COLORWAY_IMAGE[colorway] || "/products/ascent-red-white.webp";
    // Back view only exists for the full set — jackets/shorts fall back to front.
    if (view === "back" && pieceKey === "set") {
        return base.replace(".webp", "-back.webp");
    }
    return base.replace(".webp", `${p.suffix}.webp`);
}

export default function AscentDrop() {
    const [status, setStatus] = useState(null);
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [signedUp, setSignedUp] = useState(null); // { token, unlock_url }
    const [selectedColor, setSelectedColor] = useState("Red/White");
    const [selectedSize, setSelectedSize] = useState("M");
    const [selectedPiece, setSelectedPiece] = useState("set");
    const [imageView, setImageView] = useState("front"); // front | back
    const [unlockInfo, setUnlockInfo] = useState(null);
    const [params] = useSearchParams();
    const { addItem } = useCart();

    const key = params.get("key");
    const countdown = useCountdown(status?.launch_at);

    // Load public drop status
    useEffect(() => {
        fetch(`${API}/drops/${SLUG}`)
            .then((r) => r.json())
            .then(setStatus)
            .catch(() => setStatus({ launch_at: null, is_live: false, product: null }));
    }, []);

    // Try to unlock via key param
    useEffect(() => {
        if (!key) return;
        fetch(`${API}/drops/${SLUG}/unlock/${key}`)
            .then((r) => r.json())
            .then((d) => {
                setUnlockInfo(d);
                if (d?.valid) {
                    toast.success(
                        d.early_access
                            ? `Early access unlocked · Code ${d.discount_code}`
                            : `Waitlist verified. Early access opens 24hrs before launch.`,
                    );
                }
            })
            .catch(() => {});
    }, [key]);

    const isLive = status?.is_live || unlockInfo?.early_access;
    const product = status?.product;

    async function submitWaitlist(e) {
        e?.preventDefault?.();
        if (!email || submitting) return;
        setSubmitting(true);
        try {
            const r = await fetch(`${API}/drops/${SLUG}/waitlist`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });
            const data = await r.json();
            if (!r.ok) throw new Error(data?.detail || "Signup failed");
            setSignedUp(data);
            toast.success(
                data.already_joined
                    ? "You're already on the list — check your email."
                    : "Locked in. Check your inbox for the unlock link.",
            );
            // Refresh status count
            fetch(`${API}/drops/${SLUG}`).then((r) => r.json()).then(setStatus);
        } catch (err) {
            toast.error(err.message || "Something went wrong");
        } finally {
            setSubmitting(false);
        }
    }

    function handleAddToCart() {
        if (!product) return;
        const piece = PIECES.find((p) => p.key === selectedPiece) || PIECES[0];
        const pieceName = selectedPiece === "set"
            ? "ASCENT Two-Piece Set"
            : selectedPiece === "jacket" ? "ASCENT Jacket" : "ASCENT Shorts";
        addItem(
            {
                id: piece.product_id,
                name: `FGM ${pieceName} — ${COLORWAY_META[selectedColor].label}`,
                price: piece.price,
                image: pieceImage(selectedColor, selectedPiece),
            },
            selectedSize,
            selectedColor,
        );
        toast.success(
            `Added: ${pieceName} · ${COLORWAY_META[selectedColor].label} · ${selectedSize}`,
        );
    }

    const currentImage = pieceImage(selectedColor, selectedPiece, imageView);
    const currentPiece = PIECES.find((p) => p.key === selectedPiece) || PIECES[0];

    return (
        <div className="App grain min-h-screen bg-black text-white" data-testid="ascent-drop-page">
            <Header />

            {/* SEO / Share overrides */}
            <title>FGM ASCENT Collection · Limited Edition Drop</title>

            <main className="pt-24 pb-24">
                {/* HERO */}
                <section className="relative overflow-hidden border-b border-white/10">
                    <div className="absolute inset-0 pointer-events-none opacity-70 bg-[radial-gradient(circle_at_20%_30%,rgba(230,57,70,0.22),transparent_55%),radial-gradient(circle_at_80%_70%,rgba(29,78,216,0.22),transparent_55%)]" />
                    <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
                        <div className="flex items-center gap-2 mb-6">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                            </span>
                            <span className="text-red-500 text-xs tracking-[0.35em] font-display uppercase">
                                {isLive ? "Live Now" : "Coming September 1"}
                            </span>
                        </div>

                        <h1
                            data-testid="ascent-title"
                            className="font-display text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight uppercase"
                        >
                            <span className="block">FGM</span>
                            <span className="block text-red-500">Ascent</span>
                            <span className="block">Collection</span>
                        </h1>
                        <p className="mt-6 max-w-xl text-zinc-300 text-base md:text-lg font-body">
                            Limited Edition Two-Piece Set. Designed for those who refuse to settle.
                            Only 50 sets per colorway. Once it&apos;s gone, it&apos;s gone.
                        </p>
                    </div>
                </section>

                {/* COUNTDOWN */}
                {!isLive && countdown && (
                    <section
                        data-testid="ascent-countdown"
                        className="border-b border-white/10 bg-gradient-to-b from-black to-zinc-950"
                    >
                        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
                            <div className="text-center mb-8">
                                <div className="text-red-500 text-xs tracking-[0.4em] font-display uppercase mb-2">
                                    Drops In
                                </div>
                                <div className="text-white/50 text-sm tracking-widest font-display uppercase">
                                    September 1 · Midnight ET
                                </div>
                            </div>
                            <div className="flex justify-center items-start gap-4 md:gap-10">
                                <CountdownDigit label="Days" value={countdown.days} />
                                <div className="font-display text-4xl md:text-6xl text-white/20 pt-2">:</div>
                                <CountdownDigit label="Hours" value={countdown.hours} />
                                <div className="font-display text-4xl md:text-6xl text-white/20 pt-2">:</div>
                                <CountdownDigit label="Mins" value={countdown.minutes} />
                                <div className="font-display text-4xl md:text-6xl text-white/20 pt-2">:</div>
                                <CountdownDigit label="Secs" value={countdown.seconds} />
                            </div>

                            {/* Waitlist */}
                            <div className="mt-16 max-w-xl mx-auto">
                                {signedUp ? (
                                    <div
                                        data-testid="ascent-waitlist-success"
                                        className="border border-red-500/40 bg-red-500/5 p-6 md:p-8 text-center"
                                    >
                                        <div className="text-red-500 text-xs tracking-[0.35em] font-display uppercase mb-3">
                                            You&apos;re In
                                        </div>
                                        <p className="text-white text-base font-body mb-2">
                                            {signedUp.already_joined
                                                ? "You were already on the list — your unlock link is below."
                                                : "Locked in. Save this personal unlock link:"}
                                        </p>
                                        <a
                                            href={signedUp.unlock_url}
                                            data-testid="ascent-unlock-link"
                                            className="inline-block mt-3 text-white/80 hover:text-white text-xs md:text-sm font-mono break-all border-b border-white/20 hover:border-white pb-1"
                                        >
                                            {typeof window !== "undefined" ? window.location.origin : ""}
                                            {signedUp.unlock_url}
                                        </a>
                                        <p className="text-white/50 text-xs mt-4 font-body">
                                            Check your email — we&apos;ll notify you when the drop goes live.
                                        </p>
                                    </div>
                                ) : (
                                    <form
                                        onSubmit={submitWaitlist}
                                        data-testid="ascent-waitlist-form"
                                        className="border border-white/10 p-6 md:p-8"
                                    >
                                        <div className="text-red-500 text-xs tracking-[0.35em] font-display uppercase mb-2">
                                            Get Early Access
                                        </div>
                                        <h3 className="font-display text-white text-2xl md:text-3xl uppercase tracking-tight leading-tight mb-2">
                                            Join the Waitlist
                                        </h3>
                                        <p className="text-white/60 text-sm mb-6 font-body">
                                            Waitlist members unlock 24hrs before the public — plus a private discount code.
                                        </p>
                                        <div className="flex flex-col sm:flex-row gap-3">
                                            <input
                                                type="email"
                                                required
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="you@fgmapparel.com"
                                                data-testid="ascent-waitlist-email"
                                                className="flex-1 bg-black border border-white/20 focus:border-red-500 outline-none px-4 py-3 text-white text-sm font-body"
                                            />
                                            <button
                                                type="submit"
                                                disabled={submitting}
                                                data-testid="ascent-waitlist-submit"
                                                className="bg-white text-black px-8 py-3 font-display uppercase tracking-[0.2em] text-sm hover:bg-red-500 hover:text-white transition-colors disabled:opacity-50"
                                            >
                                                {submitting ? "…" : "Join →"}
                                            </button>
                                        </div>
                                        {status?.waitlist_count > 0 && (
                                            <div className="mt-4 text-white/40 text-[11px] tracking-[0.2em] font-display uppercase">
                                                {status.waitlist_count} already on the list
                                            </div>
                                        )}
                                    </form>
                                )}
                            </div>
                        </div>
                    </section>
                )}

                {/* PRODUCT SHOWCASE */}
                {product && (
                    <section className="border-b border-white/10">
                        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
                            {/* Product Image */}
                            <div className="relative">
                                <div className="aspect-square w-full bg-zinc-900 overflow-hidden ring-1 ring-white/10">
                                    <img
                                        src={currentImage}
                                        alt={`ASCENT ${selectedColor} ${imageView}`}
                                        data-testid="ascent-hero-image"
                                        className="w-full h-full object-cover"
                                    />
                                    {/* Front / Back toggle — only for Full Set */}
                                    {selectedPiece === "set" && (
                                        <div
                                            data-testid="ascent-view-toggle"
                                            className="absolute top-3 left-3 flex bg-black/70 backdrop-blur border border-white/10"
                                        >
                                            {["front", "back"].map((v) => (
                                                <button
                                                    key={v}
                                                    onClick={() => setImageView(v)}
                                                    data-testid={`ascent-view-${v}`}
                                                    className={`px-4 py-2 text-[10px] font-display uppercase tracking-[0.25em] transition-colors ${
                                                        imageView === v
                                                            ? "bg-white text-black"
                                                            : "text-white/70 hover:text-white"
                                                    }`}
                                                >
                                                    {v}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {/* Thumbnails */}
                                <div className="mt-4 grid grid-cols-3 gap-3">
                                    {Object.keys(COLORWAY_IMAGE).map((c) => (
                                        <button
                                            key={c}
                                            onClick={() => setSelectedColor(c)}
                                            data-testid={`ascent-thumb-${c.replace(/[^a-z]/gi, "-").toLowerCase()}`}
                                            className={`aspect-square overflow-hidden ring-1 transition-all ${
                                                selectedColor === c
                                                    ? "ring-red-500 ring-2"
                                                    : "ring-white/10 hover:ring-white/40"
                                            }`}
                                        >
                                            <img
                                                src={pieceImage(c, selectedPiece)}
                                                alt={c}
                                                className="w-full h-full object-cover"
                                            />
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Product Info */}
                            <div>
                                <div className="text-red-500 text-xs tracking-[0.35em] font-display uppercase mb-3">
                                    Exclusive · Limited Edition
                                </div>
                                <h2 className="font-display text-3xl md:text-4xl uppercase text-white leading-tight tracking-tight">
                                    {selectedPiece === "set"
                                        ? "FGM Ascent — Two-Piece Set"
                                        : selectedPiece === "jacket"
                                          ? "FGM Ascent — Jacket"
                                          : "FGM Ascent — Shorts"}
                                </h2>
                                <div className="mt-3 text-2xl md:text-3xl text-white font-display" data-testid="ascent-price">
                                    ${currentPiece.price.toFixed(2)}
                                    {selectedPiece === "set" && (
                                        <span className="ml-3 text-sm text-white/40 line-through font-body tracking-normal">
                                            ${(PIECES[1].price + PIECES[2].price).toFixed(2)}
                                        </span>
                                    )}
                                    {selectedPiece === "set" && (
                                        <span className="ml-2 text-xs text-red-500 tracking-[0.2em] font-display uppercase">
                                            Save ${(PIECES[1].price + PIECES[2].price - PIECES[0].price).toFixed(0)}
                                        </span>
                                    )}
                                </div>

                                {/* Piece selector — Set / Jacket / Shorts */}
                                <div className="mt-6">
                                    <div className="text-white/60 text-[11px] tracking-[0.3em] font-display uppercase mb-3">
                                        Buy As
                                    </div>
                                    <div className="grid grid-cols-3 gap-2" data-testid="ascent-piece-selector">
                                        {PIECES.map((p) => (
                                            <button
                                                key={p.key}
                                                onClick={() => {
                                                    setSelectedPiece(p.key);
                                                    if (p.key !== "set") setImageView("front");
                                                }}
                                                data-testid={`ascent-piece-${p.key}`}
                                                className={`py-3 px-2 text-xs md:text-sm font-display tracking-widest border transition-all uppercase ${
                                                    selectedPiece === p.key
                                                        ? "bg-red-500 text-white border-red-500"
                                                        : "bg-transparent text-white border-white/20 hover:border-white"
                                                }`}
                                            >
                                                {p.label}
                                                <div className="text-[10px] opacity-70 font-body tracking-normal mt-1">
                                                    ${p.price}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="mt-6 text-zinc-300 text-sm md:text-base font-body leading-relaxed whitespace-pre-line">
                                    {product.description}
                                </div>

                                {/* Colorway picker */}
                                <div className="mt-8">
                                    <div className="text-white/60 text-[11px] tracking-[0.3em] font-display uppercase mb-3">
                                        Colorway — {COLORWAY_META[selectedColor]?.label}
                                    </div>
                                    <div className="flex gap-3">
                                        {product.colors.map((c) => {
                                            const meta = COLORWAY_META[c] || { swatch: "#666", secondary: "#fff" };
                                            const selected = selectedColor === c;
                                            return (
                                                <button
                                                    key={c}
                                                    onClick={() => setSelectedColor(c)}
                                                    data-testid={`ascent-color-${c.replace(/[^a-z]/gi, "-").toLowerCase()}`}
                                                    aria-label={c}
                                                    className={`relative w-12 h-12 border-2 transition-all ${
                                                        selected ? "border-red-500 scale-110" : "border-white/20 hover:border-white/60"
                                                    }`}
                                                    style={{
                                                        background: `linear-gradient(135deg, ${meta.swatch} 50%, ${meta.secondary} 50%)`,
                                                    }}
                                                />
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Size picker */}
                                <div className="mt-8">
                                    <div className="text-white/60 text-[11px] tracking-[0.3em] font-display uppercase mb-3">
                                        Size
                                    </div>
                                    <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
                                        {product.sizes.map((s) => (
                                            <button
                                                key={s}
                                                onClick={() => setSelectedSize(s)}
                                                data-testid={`ascent-size-${s}`}
                                                className={`py-3 text-sm font-display tracking-widest border transition-all ${
                                                    selectedSize === s
                                                        ? "bg-white text-black border-white"
                                                        : "bg-transparent text-white border-white/20 hover:border-white"
                                                }`}
                                            >
                                                {s}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Discount code strip */}
                                {unlockInfo?.valid && unlockInfo?.discount_code && (
                                    <div className="mt-6 p-4 border border-red-500/40 bg-red-500/5">
                                        <div className="text-red-500 text-[11px] tracking-[0.3em] font-display uppercase mb-1">
                                            Waitlist Perk
                                        </div>
                                        <div className="text-white text-sm font-body">
                                            Use code <span className="font-mono text-red-400 font-bold">{unlockInfo.discount_code}</span> at checkout for your private discount.
                                        </div>
                                    </div>
                                )}

                                {/* Add to cart or Locked */}
                                <div className="mt-8">
                                    {isLive ? (
                                        <button
                                            onClick={handleAddToCart}
                                            data-testid="ascent-add-to-cart"
                                            className="w-full bg-red-500 hover:bg-red-600 text-white py-4 font-display uppercase tracking-[0.3em] text-sm transition-colors"
                                        >
                                            Add to Cart · ${currentPiece.price.toFixed(2)}
                                        </button>
                                    ) : (
                                        <button
                                            disabled
                                            data-testid="ascent-locked-btn"
                                            className="w-full bg-white/5 text-white/40 py-4 font-display uppercase tracking-[0.3em] text-sm border border-white/10 cursor-not-allowed"
                                        >
                                            🔒 Unlocks September 1
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* FEATURES */}
                <section className="border-b border-white/10">
                    <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
                        <div className="text-center mb-12">
                            <div className="text-red-500 text-xs tracking-[0.35em] font-display uppercase mb-2">
                                Details
                            </div>
                            <h2 className="font-display text-3xl md:text-4xl uppercase text-white leading-tight">
                                Every Stitch Considered
                            </h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                            {[
                                { title: "Premium Woven Fabric", body: "Lightweight technical woven with a subtle sheen — breathable through the grind, sharp for the after." },
                                { title: "Matching Two-Piece", body: "Zip-up short-sleeve jacket + matching shorts. Wear together or split — every combo built to move." },
                                { title: "Embroidered FGM", body: "Raised embroidered eagle crest — no prints, no vinyl. Built to last past this drop and the next." },
                                { title: "Athletic Modern Fit", body: "Cut for movement — sculpted through the torso, roomy through the leg." },
                                { title: "Elastic Waist + Drawstring", body: "Adjustable drawstring, elastic waistband — dialed to you." },
                                { title: "Side Pockets", body: "Real pockets on the shorts. Because we test what we sell." },
                            ].map((f) => (
                                <div key={f.title} className="border border-white/10 p-6 hover:border-white/30 transition-colors">
                                    <div className="text-red-500 text-[11px] tracking-[0.3em] font-display uppercase mb-2">
                                        Feature
                                    </div>
                                    <h3 className="font-display text-white text-lg uppercase tracking-tight mb-2">
                                        {f.title}
                                    </h3>
                                    <p className="text-white/60 text-sm font-body leading-relaxed">
                                        {f.body}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CLOSING */}
                <section>
                    <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24 text-center">
                        <p className="font-display text-3xl md:text-5xl text-white uppercase leading-tight tracking-tight">
                            Focus. <span className="text-red-500">Grind.</span> Motivate.
                        </p>
                        <p className="mt-6 text-white/50 font-body">
                            Not on the list yet?{" "}
                            <Link to="/#drop-teaser" className="text-red-500 hover:text-red-400 border-b border-red-500/40">
                                Head back to the top and join the waitlist
                            </Link>
                            .
                        </p>
                    </div>
                </section>
            </main>

            <Footer />
            <CartDrawer />
        </div>
    );
}
